import { Component } from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { jsPDF } from "jspdf";

@Component({
  selector: "app-mantenimientos-vehiculos",
  templateUrl: "./mantenimientos-vehiculos.component.html",
  styleUrls: ["./mantenimientos-vehiculos.component.scss"],
})
export class MantenimientosVehiculosComponent {
  documentos = [
    {
      id: 1,
      nombre: "Procedimiento de Mantenimiento Preventivo de la Flota",
    },
  ];
  pdfUrl: SafeResourceUrl | null = null;
  pdfNombre: string | null = null;
  mostrarModal = false;

  private blobUrl: string | null = null;

  constructor(private sanitizer: DomSanitizer) {}

  abrirDocumento(): void {
    this.generarProcedimiento();
  }

  private generarProcedimiento(): void {
    const pdf = new jsPDF();

    const margen = 15;
    const ancho = 180;
    const margenInferior = 270; // límite vertical antes de forzar salto de página
    const yInicioSeccion = 30;

    // --------------------------------------------------------
    // Paleta de colores
    // --------------------------------------------------------
    const colorPrimario: [number, number, number] = [24, 58, 92]; // azul corporativo
    const colorAcento: [number, number, number] = [0, 141, 137]; // verde azulado
    const colorTexto: [number, number, number] = [55, 55, 55];
    const colorTextoSuave: [number, number, number] = [110, 110, 110];
    const colorLinea: [number, number, number] = [215, 220, 224];

    const setColorTexto = (c: [number, number, number]) =>
      pdf.setTextColor(c[0], c[1], c[2]);
    const setColorRelleno = (c: [number, number, number]) =>
      pdf.setFillColor(c[0], c[1], c[2]);
    const setColorTrazo = (c: [number, number, number]) =>
      pdf.setDrawColor(c[0], c[1], c[2]);

    const nombreDocumento = "Procedimiento de Mantenimiento Preventivo de la Flota";

    // --------------------------------------------------------
    // Helpers de layout
    // --------------------------------------------------------

    // Si el contenido que sigue no cabe en lo que resta de la página,
    // agrega una página nueva y devuelve el "y" inicial de esa página.
    const verificarSalto = (y: number, espacioNecesario: number = 10): number => {
      if (y + espacioNecesario > margenInferior) {
        pdf.addPage();
        return yInicioSeccion;
      }
      return y;
    };

    const titulo = (texto: string, y: number = yInicioSeccion): number => {
      // Barra de acento a la izquierda del título
      setColorRelleno(colorAcento);
      pdf.rect(margen, y - 6, 3, 9, "F");

      setColorTexto(colorPrimario);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(16);
      pdf.text(texto, margen + 7, y);

      // Línea divisoria debajo del título
      setColorTrazo(colorLinea);
      pdf.setLineWidth(0.4);
      pdf.line(margen, y + 4, margen + ancho, y + 4);

      setColorTexto(colorTexto);
      return y + 13;
    };

    const subtitulo = (texto: string, y: number): number => {
      y = verificarSalto(y, 16);
      setColorRelleno(colorAcento);
      pdf.circle(margen + 1.3, y - 1.6, 1.3, "F");

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(13);
      setColorTexto(colorPrimario);
      pdf.text(texto, margen + 6, y);

      setColorTexto(colorTexto);
      return y + 8;
    };

    const parrafo = (texto: string, y: number): number => {
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(11);
      setColorTexto(colorTexto);

      const lineas = pdf.splitTextToSize(texto, ancho);
      const alturaTexto = lineas.length * 6;

      y = verificarSalto(y, alturaTexto);
      pdf.text(lineas, margen, y);

      return y + alturaTexto + 4;
    };

    const lista = (items: string[], y: number): number => {
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(11);

      items.forEach((item) => {
        y = verificarSalto(y, 8);

        const lineas = pdf.splitTextToSize(item, ancho - 12);

        // Punto de viñeta coloreado
        setColorRelleno(colorAcento);
        pdf.circle(margen + 2, y - 1.4, 1, "F");

        setColorTexto(colorTexto);
        pdf.text(lineas, margen + 7, y);
        y += lineas.length * 7;
      });

      return y + 3;
    };

    // Etiqueta corta en negrita (p. ej. "Conductor:") con color de acento
    const etiqueta = (texto: string, y: number): number => {
      y = verificarSalto(y, 10);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(11);
      setColorTexto(colorAcento);
      pdf.text(texto, margen, y);
      setColorTexto(colorTexto);
      return y + 7;
    };

    // Definición: término en negrita seguido de su explicación, con viñeta.
    const definicion = (termino: string, texto: string, y: number): number => {
      pdf.setFontSize(11);

      pdf.setFont("helvetica", "bold");
      const anchoTermino = pdf.getTextWidth(termino + ": ");

      pdf.setFont("helvetica", "normal");
      const lineas = pdf.splitTextToSize(texto, ancho - 7 - anchoTermino);
      const alturaTexto = lineas.length * 6 + 2;

      y = verificarSalto(y, alturaTexto);

      setColorRelleno(colorAcento);
      pdf.circle(margen + 2, y - 1.4, 1, "F");

      pdf.setFont("helvetica", "bold");
      setColorTexto(colorPrimario);
      pdf.text(termino + ": ", margen + 7, y);

      pdf.setFont("helvetica", "normal");
      setColorTexto(colorTexto);
      pdf.text(lineas[0], margen + 7 + anchoTermino, y);

      // Resto de líneas (si las hay) ocupan todo el ancho disponible
      if (lineas.length > 1) {
        const resto = pdf.splitTextToSize(
          lineas.slice(1).join(" "),
          ancho - 7
        );
        pdf.text(resto, margen + 7, y + 6);
        y += resto.length * 6;
      }

      return y + 8;
    };

    // Escribe el título de una sección de forma continua: si no hay espacio
    // suficiente en la página actual para el título + algo de contenido,
    // salta de página; si sí hay espacio, sigue en la misma página
    // (así se evitan hojas con mucho espacio en blanco).
    const nuevaSeccion = (textoTitulo: string, y: number): number => {
      y = verificarSalto(y, 40);
      return titulo(textoTitulo, y);
    };

    // ============================================================
    // PORTADA
    // ============================================================

    // Banda superior de color
    setColorRelleno(colorPrimario);
    pdf.rect(0, 0, 210, 55, "F");

    setColorRelleno(colorAcento);
    pdf.rect(0, 55, 210, 3, "F");

    setColorTexto([255, 255, 255]);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(13);
    pdf.text("PLAN ESTRATÉGICO DE SEGURIDAD VIAL - PESV", 105, 32, {
      align: "center",
    });

    // Título principal
    setColorTexto(colorPrimario);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(22);
    pdf.text(
      pdf.splitTextToSize(
        "PROCEDIMIENTO PARA EL MANTENIMIENTO PREVENTIVO DE LA FLOTA",
        160
      ),
      105,
      100,
      { align: "center" }
    );

    setColorRelleno(colorAcento);
    pdf.rect(75, 122, 60, 1.2, "F");

    setColorTexto(colorAcento);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(15);
    pdf.text("MEDIANTE PLANES, PROGRAMACIÓN Y ÓRDENES DE MANTENIMIENTO", 105, 134, {
      align: "center",
      maxWidth: 170,
    });

    setColorTexto(colorTextoSuave);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(11);
    pdf.text(
      "Control y trazabilidad del mantenimiento de los vehículos y motocicletas",
      105,
      152,
      { align: "center" }
    );

    // Banda inferior de color con fecha de generación
    setColorRelleno(colorPrimario);
    pdf.rect(0, 277, 210, 20, "F");

    setColorTexto([255, 255, 255]);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    const fechaGeneracion = new Date().toLocaleDateString("es-CO", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
    pdf.text(`Documento generado el ${fechaGeneracion}`, 105, 289, {
      align: "center",
    });

    setColorTexto(colorTexto);

    // ============================================================
    // 1. OBJETIVO - ALCANCE - RESPONSABLES
    // ============================================================

    pdf.addPage(); // primera página de contenido, después de la portada
    let y = nuevaSeccion("1. Objetivo, Alcance y Responsables", yInicioSeccion);

    y = subtitulo("1.1 Objetivo", y);

    y = parrafo(
      "Establecer el procedimiento para planificar, programar, ejecutar y hacer seguimiento al mantenimiento preventivo de los vehículos y motocicletas de la organización, con el fin de conservar su buen estado técnico-mecánico, prevenir fallas que puedan generar siniestros viales y garantizar el cumplimiento del Plan Estratégico de Seguridad Vial (PESV).",
      y
    );

    y = subtitulo("1.2 Alcance", y);

    y = parrafo(
      "Aplica a todos los vehículos y motocicletas propios, en leasing o afiliados que hagan parte de la operación de la organización y que cumplan con los criterios definidos en cada plan de mantenimiento (clase, marca, combustible, modelo y cilindraje).",
      y
    );

    y = subtitulo("1.3 Responsables", y);

    y = etiqueta("Administrador o Coordinador de mantenimiento:", y);

    y = lista(
      [
        "Crear, actualizar, activar e inactivar los planes de mantenimiento.",
        "Definir las actividades de cada plan con sus frecuencias y umbrales de alerta.",
        "Generar y hacer seguimiento a las programaciones de mantenimiento.",
        "Generar las órdenes de mantenimiento y gestionar su cierre.",
      ],
      y
    );

    y = etiqueta("Conductor:", y);

    y = lista(
      [
        "Registrar el kilometraje actual del vehículo en la inspección diaria.",
        "Reportar fallas o novedades detectadas durante la operación.",
        "Entregar el vehículo para el mantenimiento en la fecha programada.",
      ],
      y
    );

    y = etiqueta("Supervisor o Líder del PESV:", y);

    y = lista(
      [
        "Verificar el cumplimiento de las programaciones.",
        "Priorizar los mantenimientos próximos o vencidos.",
        "Asegurar que no operen vehículos con mantenimientos obligatorios vencidos que representen riesgo.",
      ],
      y
    );

    y = etiqueta("Taller o proveedor de mantenimiento:", y);

    y = lista(
      [
        "Ejecutar las actividades indicadas en la orden de mantenimiento.",
        "Informar las novedades y repuestos intervenidos.",
        "Entregar los soportes del servicio realizado.",
      ],
      y
    );

    // ============================================================
    // 2. DEFINICIONES
    // ============================================================

    y = nuevaSeccion("2. Definiciones", y);

    y = definicion(
      "Plan de mantenimiento",
      "Conjunto de actividades de mantenimiento aplicables a los vehículos que cumplen unos criterios comunes de clase, marca, combustible, modelo y cilindraje.",
      y
    );

    y = definicion(
      "Actividad",
      "Servicio de mantenimiento a realizar dentro del plan, asociado a un sistema o parte del vehículo.",
      y
    );

    y = definicion(
      "Tipo de control",
      "Criterio con el que se controla la actividad: por kilometraje (KM), por tiempo (MESES) o por ambos (KM y MESES).",
      y
    );

    y = definicion(
      "Frecuencia",
      "Intervalo de kilometraje o de meses con el que debe repetirse la actividad.",
      y
    );

    y = definicion(
      "Umbral de alerta",
      "Margen en kilómetros o en días, previo al vencimiento, a partir del cual el sistema advierte que el mantenimiento está próximo.",
      y
    );

    y = definicion(
      "Actividad obligatoria",
      "Actividad del plan que debe ejecutarse siempre y no puede omitirse.",
      y
    );

    y = definicion(
      "Programación",
      "Registro generado para cada vehículo y actividad con su último mantenimiento, próxima fecha o kilometraje y estado.",
      y
    );

    y = definicion(
      "Orden de mantenimiento",
      "Documento que formaliza la ejecución de una o varias actividades programadas sobre un vehículo.",
      y
    );

    // ============================================================
    // 3. PROCEDIMIENTO
    // ============================================================

    y = nuevaSeccion("3. Procedimiento", y);

    y = subtitulo("3.1 Creación del plan de mantenimiento", y);

    y = parrafo(
      "El administrador ingresa al módulo de mantenimientos y crea un nuevo plan diligenciando la siguiente información:",
      y
    );

    y = lista(
      [
        "Clase de vehículo, marca y tipo de combustible (obligatorios).",
        "Rango de modelos (años): desde y hasta, o la opción Todos.",
        "Rango de cilindraje (cc): desde y hasta, o la opción Todos.",
        "Descripción detallada del plan.",
      ],
      y
    );

    y = parrafo(
      "El nombre del plan se genera automáticamente a partir de la clase, la marca y el combustible seleccionados. Mientras se diligencia el formulario, el sistema muestra un resumen con el porcentaje de avance y el alcance del plan, indicando a qué vehículos aplicará.",
      y
    );

    y = subtitulo("3.2 Gestión de los planes", y);

    y = parrafo(
      "El listado de planes permite consultar el código, el tipo de vehículo, la marca, el combustible, los modelos, los cilindrajes y el estado de cada plan. Se puede filtrar por texto, tipo de vehículo, combustible y estado (activos o inactivos).",
      y
    );

    y = lista(
      [
        "Editar un plan existente.",
        "Inactivar un plan: deja de generarse en nuevas órdenes de trabajo; los vehículos que ya lo tienen asignado no se ven afectados.",
        "Activar nuevamente un plan inactivo para que pueda asignarse a órdenes de trabajo.",
      ],
      y
    );

    y = subtitulo("3.3 Registro de actividades del plan", y);

    y = parrafo(
      "Desde un plan activo se accede a sus actividades. En esta pantalla el sistema muestra los criterios del plan y los vehículos compatibles. Para cada actividad se registra:",
      y
    );

    y = lista(
      [
        "Actividad a realizar (servicio de mantenimiento).",
        "Frecuencia en kilómetros y alerta en kilómetros.",
        "Frecuencia en meses y alerta en días; la alerta debe ser menor a la equivalencia en días de la frecuencia en meses.",
        "Indicador de actividad obligatoria.",
      ],
      y
    );

    y = parrafo(
      "Las actividades se pueden editar o eliminar antes de guardar. Los cambios solo se aplican cuando se presiona el botón Guardar. La pestaña Vehículos que cumplen lista las placas que cumplen los criterios del plan, con su marca, modelo, cilindraje, tipo de vehículo y combustible.",
      y
    );

    y = subtitulo("3.4 Programación de mantenimientos", y);

    y = parrafo(
      "Desde el plan o desde un vehículo compatible se accede a la proyección de la programación. El sistema agrupa las actividades por vehículo y muestra, para cada una:",
      y
    );

    y = lista(
      [
        "Último kilometraje y último registro del mantenimiento.",
        "Próximo kilometraje.",
        "Frecuencia y umbral de alerta.",
        "Estado de la actividad.",
      ],
      y
    );

    y = parrafo(
      "El administrador selecciona las actividades que desea programar, de forma individual, por vehículo o todas a la vez, y confirma con la opción Agregar a programación. Con esto se generan las programaciones de mantenimiento para los vehículos y actividades seleccionados.",
      y
    );

    y = subtitulo("3.5 Seguimiento de las programaciones", y);

    y = parrafo(
      "En el módulo de programación se consultan las programaciones filtrando por estado de programación, rango de fechas, vehículo y plan de mantenimiento. Cada registro muestra:",
      y
    );

    y = lista(
      [
        "Fecha, vehículo, código del plan y actividad.",
        "Criterio de frecuencia (KM o MESES).",
        "Último y próximo mantenimiento.",
        "Restante en kilómetros o en días.",
        "Umbral e inicio de alerta.",
        "Estado actual: PROGRAMADO, PRÓXIMO o VENCIDO.",
      ],
      y
    );

    y = parrafo(
      "El detalle de cada programación permite revisar la información del vehículo, del mantenimiento, el control por kilometraje o por tiempo, y los datos de registro (fecha, usuario y fecha de creación). Una actividad puede cancelarse y reactivarse cuando sea necesario.",
      y
    );

    y = subtitulo("3.6 Generación de la orden de mantenimiento", y);

    y = parrafo(
      "Las actividades en estado próximo o vencido de un mismo vehículo pueden seleccionarse para generar una orden de mantenimiento. Para ello se diligencia:",
      y
    );

    y = lista(
      [
        "Tipo de mantenimiento y prioridad.",
        "Fecha programada.",
        "Kilometraje de ingreso.",
        "Motivo del mantenimiento.",
        "Observaciones iniciales.",
      ],
      y
    );

    y = parrafo(
      "La orden queda vinculada al vehículo, al plan, a la actividad y a la programación, lo que garantiza la trazabilidad del mantenimiento.",
      y
    );

    // ============================================================
    // 4. ESTADOS Y ALERTAS
    // ============================================================

    y = nuevaSeccion("4. Estados y Alertas", y);

    y = parrafo(
      "El sistema calcula el estado de cada programación a partir del kilometraje actual del vehículo o de la fecha, según el tipo de control definido.",
      y
    );

    y = definicion(
      "PROGRAMADO",
      "El mantenimiento aún no ha alcanzado el umbral de alerta.",
      y
    );

    y = definicion(
      "PRÓXIMO",
      "El vehículo ya superó el umbral de alerta y se acerca al vencimiento; debe gestionarse la orden de mantenimiento.",
      y
    );

    y = definicion(
      "VENCIDO",
      "Se superó el kilometraje o la fecha límite; se debe atender de inmediato y evaluar el riesgo de operación del vehículo.",
      y
    );

    y = parrafo(
      "Si la condición del vehículo representa un riesgo para la seguridad vial, no podrá continuar la operación hasta que se ejecute el mantenimiento correspondiente.",
      y
    );

    // ============================================================
    // 5. CONSERVACIÓN DE LOS REGISTROS
    // ============================================================

    y = nuevaSeccion("5. Conservación de los Registros", y);

    y = lista(
      [
        "Planes de mantenimiento y sus actividades.",
        "Programaciones generadas y su estado.",
        "Órdenes de mantenimiento.",
        "Kilometraje y fecha de cada mantenimiento.",
        "Usuario y fecha de cada registro.",
        "Historial de cambios de estado.",
      ],
      y
    );

    y = subtitulo("Requisitos", y);

    y = lista(
      [
        "Almacenamiento digital.",
        "Integridad de la información.",
        "Disponibilidad.",
        "Trazabilidad.",
        "Acceso restringido.",
        "Conservación según TRD.",
      ],
      y
    );

    // ============================================================
    // 6. CONTROL Y SEGUIMIENTO / 7. REGISTROS ASOCIADOS
    // ============================================================

    y = nuevaSeccion("6. Control y Seguimiento", y);

    y = lista(
      [
        "Cumplimiento de las programaciones de mantenimiento.",
        "Mantenimientos próximos y vencidos.",
        "Actividades obligatorias pendientes.",
        "Cierre de órdenes de mantenimiento.",
        "Indicadores del proceso.",
      ],
      y
    );

    y = subtitulo("7. Registros Asociados", y);

    y = lista(
      [
        "Plan de mantenimiento.",
        "Listado de actividades del plan.",
        "Programación de mantenimientos.",
        "Orden de mantenimiento.",
        "Historial de kilometraje.",
        "Soportes del servicio realizado.",
      ],
      y
    );

    // ============================================================
    // ENCABEZADO Y PIE DE PÁGINA (en todas las páginas de contenido)
    // ============================================================

    const totalPaginas = pdf.getNumberOfPages();

    for (let pagina = 2; pagina <= totalPaginas; pagina++) {
      pdf.setPage(pagina);

      // Encabezado: nombre del documento + línea de acento
      setColorTexto(colorTextoSuave);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.text(nombreDocumento, margen, 12);

      setColorRelleno(colorAcento);
      pdf.rect(margen, 15, ancho, 0.6, "F");

      // Pie de página: línea + numeración
      setColorTrazo(colorLinea);
      pdf.setLineWidth(0.3);
      pdf.line(margen, 283, margen + ancho, 283);

      setColorTexto(colorTextoSuave);
      pdf.setFontSize(8);
      pdf.text("PESV - Seguridad Vial", margen, 289);
      pdf.text(`Página ${pagina - 1} de ${totalPaginas - 1}`, margen + ancho, 289, {
        align: "right",
      });
    }

    this.mostrarPdfEnModal(pdf, nombreDocumento);
  }

  private mostrarPdfEnModal(pdf: jsPDF, nombre: string): void {
    const blob = pdf.output("blob");
    this.blobUrl = URL.createObjectURL(blob);

    this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.blobUrl);
    this.pdfNombre = nombre;
    this.mostrarModal = true;
  }

  cerrarModal(): void {
    this.mostrarModal = false;

    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }

    this.pdfUrl = null;
    this.pdfNombre = null;
  }
}